/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useEffect, useState } from "react";
import {
  Box,
  SimpleGrid,
  Text,
  Heading,
  Badge,
  VStack,
  HStack,
  Divider,
  Button,
  Center,
  Spinner,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  FormControl,
  FormLabel,
  Switch,
  Textarea,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Td,
  Tr,
  Thead,
  Th,
  Table,
  Tbody,
} from "@chakra-ui/react";
import { FaEdit, FaPlus, FaTrash } from "react-icons/fa";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../hooks/useTheme";
import PageContainer from "../../components/PageContainer";
import { formatNumber } from "../../../utils/formatNumber";

export default function Plans() {
  const { t } = useTranslation();
  const { isArabic, colors, dir, isDark } = useTheme();
  const toast = useToast();

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, '');

  const safeString = (val) => {
    if (val === null || val === undefined || typeof val === "object") return "";
    return String(val).trim();
  };

  // جلب الباقات مع منع التكرار تمامًا
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const token = localStorage.getItem("authToken");

        const res = await fetch(`${baseUrl}/Plans/GetPlan?PlanId=-1`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
        });

        if (!res.ok) throw new Error("Failed to fetch plans");

        const data = await res.json();

        if (Array.isArray(data)) {
          const uniquePlans = [];
          const seenIds = new Set();

          data.forEach((p) => {
            const id = p.planId || p.id || 0;
            if (id && !seenIds.has(id)) {
              seenIds.add(id);
              uniquePlans.push({
                planId: id,
                planType: safeString(p.planType),
                engPlanType: safeString(p.engPlanType),
                price: parseFloat(p.price) || 0,
                descripsion: safeString(p.descripsion),
                engDescripsion: safeString(p.engDescripsion),
                isActive: p.isActive ?? 1,
                note1: safeString(p.note1), note2: safeString(p.note2), note3: safeString(p.note3),
                note4: safeString(p.note4), note5: safeString(p.note5), note6: safeString(p.note6),
                note7: safeString(p.note7), note8: safeString(p.note8),
                engNote1: safeString(p.engNote1), engNote2: safeString(p.engNote2),
                engNote3: safeString(p.engNote3), engNote4: safeString(p.engNote4),
                engNote5: safeString(p.engNote5), engNote6: safeString(p.engNote6),
                engNote7: safeString(p.engNote7), engNote8: safeString(p.engNote8),
                creationDate: p.creationDate || "",
              });
            }
          });

          setPlans(uniquePlans);
        }
      } catch (err) {
        console.error(err);
        toast({ title: isArabic ? "فشل تحميل الباقات" : "Failed to load plans", status: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, [isArabic, toast, baseUrl]);

  const openCreateModal = () => {
    setSelectedPlan({
      planId: 0,
      planType: "",
      engPlanType: "",
      price: 0,
      descripsion: "",
      engDescripsion: "",
      isActive: 1,
      note1: "", note2: "", note3: "", note4: "", note5: "", note6: "", note7: "", note8: "",
      engNote1: "", engNote2: "", engNote3: "", engNote4: "", engNote5: "", engNote6: "", engNote7: "", engNote8: "",
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!selectedPlan.planType?.trim() && !selectedPlan.engPlanType?.trim()) {
      toast({ title: isArabic ? "اسم الباقة مطلوب" : "Plan name is required", status: "error" });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("authToken");

      const payload = {
        ...(selectedPlan.planId > 0 && { planId: selectedPlan.planId }),
        planType: selectedPlan.planType || "",
        engPlanType: selectedPlan.engPlanType || "",
        price: Number(selectedPlan.price) || 0,
        descripsion: selectedPlan.descripsion || "",
        engDescripsion: selectedPlan.engDescripsion || "",
        isActive: selectedPlan.isActive,
        note1: selectedPlan.note1 || "", note2: selectedPlan.note2 || "",
        note3: selectedPlan.note3 || "", note4: selectedPlan.note4 || "",
        note5: selectedPlan.note5 || "", note6: selectedPlan.note6 || "",
        note7: selectedPlan.note7 || "", note8: selectedPlan.note8 || "",
        engNote1: selectedPlan.engNote1 || "", engNote2: selectedPlan.engNote2 || "",
        engNote3: selectedPlan.engNote3 || "", engNote4: selectedPlan.engNote4 || "",
        engNote5: selectedPlan.engNote5 || "", engNote6: selectedPlan.engNote6 || "",
        engNote7: selectedPlan.engNote7 || "", engNote8: selectedPlan.engNote8 || "",
      };

      const res = await fetch(`${baseUrl}/Plans/SavePlan`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || "Save failed");
      }

      toast({
        title: selectedPlan.planId > 0
          ? (isArabic ? "تم التحديث بنجاح" : "Updated successfully")
          : (isArabic ? "تم الإضافة بنجاح" : "Added successfully"),
        status: "success",
      });

      // تحديث القائمة بعد الحفظ
      const refreshRes = await fetch(`${baseUrl}/Plans/GetPlan?PlanId=-1`, {
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });

      if (refreshRes.ok) {
        const data = await refreshRes.json();
        if (Array.isArray(data)) {
          const unique = [];
          const seen = new Set();
          data.forEach(p => {
            const id = p.planId || p.id || 0;
            if (id && !seen.has(id)) {
              seen.add(id);
              unique.push({
                planId: id,
                planType: safeString(p.planType),
                engPlanType: safeString(p.engPlanType),
                price: parseFloat(p.price) || 0,
                descripsion: safeString(p.descripsion),
                engDescripsion: safeString(p.engDescripsion),
                isActive: p.isActive ?? 1,
                note1: safeString(p.note1), note2: safeString(p.note2), note3: safeString(p.note3),
                note4: safeString(p.note4), note5: safeString(p.note5), note6: safeString(p.note6),
                note7: safeString(p.note7), note8: safeString(p.note8),
                engNote1: safeString(p.engNote1), engNote2: safeString(p.engNote2),
                engNote3: safeString(p.engNote3), engNote4: safeString(p.engNote4),
                engNote5: safeString(p.engNote5), engNote6: safeString(p.engNote6),
                engNote7: safeString(p.engNote7), engNote8: safeString(p.engNote8),
              });
            }
          });
          setPlans(unique);
        }
      }

      setIsModalOpen(false);
    } catch (err) {
      toast({ title: isArabic ? "فشل الحفظ" : "Save failed", description: err.message, status: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (planId) => {
    if (!planId || planId <= 0) return;

    const result = await Swal.fire({
      title: isArabic ? "تأكيد الحذف" : "Confirm Delete",
      text: isArabic ? "هل أنت متأكد من حذف هذه الباقة نهائيًا؟" : "Are you sure you want to delete this plan permanently?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: isArabic ? "نعم، احذف!" : "Yes, delete!",
      cancelButtonText: isArabic ? "إلغاء" : "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      const token = localStorage.getItem("authToken");

      const res = await fetch(`${baseUrl}/Plans/DeletePlan?PlanId=${planId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
      });

      if (!res.ok) throw new Error("Delete failed");

      // حذف فقط الباقة اللي لها نفس planId بالضبط
      setPlans(prev => prev.filter(p => p.planId !== planId));

      Swal.fire({
        title: isArabic ? "تم الحذف!" : "Deleted!",
        text: isArabic ? "تم حذف الباقة بنجاح" : "Plan deleted successfully",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        title: isArabic ? "فشل الحذف" : "Delete Failed",
        text: err.message,
        icon: "error",
      });
    }
  };

  if (loading) {
    return (
      <Center minH="80vh">
        <Spinner size="xl" color={colors.mainColor} />
      </Center>
    );
  }

  const getDisplayName = (plan) => isArabic ? (plan.planType || plan.engPlanType || "—") : (plan.engPlanType || plan.planType || "—");
  const getDisplayDesc = (plan) => isArabic ? (plan.descripsion || plan.engDescripsion || "—") : (plan.engDescripsion || plan.descripsion || "—");

  return (
    <PageContainer title={isArabic ? "إدارة الباقات" : "Plans Management"}>
      <HStack mb={6} justify="flex-end">
        <Button leftIcon={<FaPlus />} colorScheme="purple" onClick={openCreateModal}>
          {isArabic ? "إضافة باقة جديدة" : "Add New Plan"}
        </Button>
      </HStack>

      <Box overflowX="auto">
        <Table
          variant="simple"
          bg={colors.cardBg}
          shadow="md"
          borderRadius="lg"
          color={colors.textColor}
        >
          <Thead bg={colors.tableHeaderBg}>
            <Tr>
              <Th color={colors.textColor} borderColor={colors.tableBorder}>
                {isArabic ? "اسم الباقة" : "Plan Name"}
              </Th>
              <Th textAlign="center" color={colors.textColor} borderColor={colors.tableBorder}>
                {isArabic ? "السعر" : "Price"}
              </Th>
              <Th color={colors.textColor} borderColor={colors.tableBorder}>
                {isArabic ? "الوصف" : "Description"}
              </Th>
              <Th textAlign="center" color={colors.textColor} borderColor={colors.tableBorder}>
                {isArabic ? "الإجراءات" : "Actions"}
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {plans.map((plan) => (
              <Tr
                key={plan.planId}
                _hover={{ bg: colors.tableRowHover }}
                borderColor={colors.tableBorder}
              >
                <Td fontWeight="medium" color={colors.textColor} borderColor={colors.tableBorder}>
                  {getDisplayName(plan)}
                </Td>
                <Td textAlign="center" borderColor={colors.tableBorder}>
                  <Badge colorScheme="blue" px={3} py={1} borderRadius="full">
                    IQD {formatNumber(plan.price)}
                  </Badge>
                </Td>
                <Td
                  maxW="350px"
                  isTruncated
                  title={getDisplayDesc(plan)}
                  color={colors.subText}
                  borderColor={colors.tableBorder}
                >
                  {getDisplayDesc(plan)}
                </Td>
                <Td borderColor={colors.tableBorder}>
                  <HStack spacing={3} justify="center">
                    <Button
                      size="sm"
                      leftIcon={<FaEdit />}
                      colorScheme="purple"
                      onClick={() => {
                        setSelectedPlan(plan);
                        setIsModalOpen(true);
                      }}
                    >
                      {isArabic ? "تعديل" : "Edit"}
                    </Button>
                    <Button
                      size="sm"
                      leftIcon={<FaTrash />}
                      colorScheme="red"
                      onClick={() => handleDelete(plan.planId)}
                    >
                      {isArabic ? "حذف" : "Delete"}
                    </Button>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* Modal كامل للإضافة والتعديل */}
      {selectedPlan && (
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="xl" scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent dir={dir}>
            <ModalHeader pr={isArabic ? "40px" : "8"} pl={isArabic ? "8" : "40px"}>
              {selectedPlan.planId > 0
                ? (isArabic ? "تعديل الباقة" : "Edit Plan")
                : (isArabic ? "إضافة باقة جديدة" : "Add New Plan")}
            </ModalHeader>
            <ModalCloseButton top={4} right={isArabic ? 4 : "auto"} left={isArabic ? "auto" : 4} />
            <ModalBody>
              <VStack spacing={5} align="stretch">
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel>{isArabic ? "اسم الباقة (عربي)" : "Plan Name (Arabic)"}</FormLabel>
                    <Input
                      value={selectedPlan.planType || ""}
                      onChange={(e) => setSelectedPlan({ ...selectedPlan, planType: e.target.value })}
                      bg={colors.inputBg}
                      color={colors.textColor}
                      borderColor={colors.inputBorder}
                      _focus={{ borderColor: colors.inputFocus, boxShadow: `0 0 0 1px ${colors.inputFocus}` }}
                      _hover={{ borderColor: colors.inputFocus }}
                      sx={{
                        "&": {
                          bg: `${colors.inputBg} !important`,
                          color: `${colors.textColor} !important`,
                        },
                      }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>{isArabic ? "اسم الباقة (إنجليزي)" : "Plan Name (English)"}</FormLabel>
                    <Input
                      value={selectedPlan.engPlanType || ""}
                      onChange={(e) => setSelectedPlan({ ...selectedPlan, engPlanType: e.target.value })}
                      bg={colors.inputBg}
                      color={colors.textColor}
                      borderColor={colors.inputBorder}
                      _focus={{ borderColor: colors.inputFocus, boxShadow: `0 0 0 1px ${colors.inputFocus}` }}
                      _hover={{ borderColor: colors.inputFocus }}
                      sx={{
                        "&": {
                          bg: `${colors.inputBg} !important`,
                          color: `${colors.textColor} !important`,
                        },
                      }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>{isArabic ? "الوصف (عربي)" : "Description (Arabic)"}</FormLabel>
                    <Textarea
                      value={selectedPlan.descripsion || ""}
                      onChange={(e) => setSelectedPlan({ ...selectedPlan, descripsion: e.target.value })}
                      rows={3}
                      bg={colors.inputBg}
                      color={colors.textColor}
                      borderColor={colors.inputBorder}
                      _focus={{ borderColor: colors.inputFocus, boxShadow: `0 0 0 1px ${colors.inputFocus}` }}
                      _hover={{ borderColor: colors.inputFocus }}
                      sx={{
                        "&": {
                          bg: `${colors.inputBg} !important`,
                          color: `${colors.textColor} !important`,
                        },
                      }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>{isArabic ? "الوصف (إنجليزي)" : "Description (English)"}</FormLabel>
                    <Textarea
                      value={selectedPlan.engDescripsion || ""}
                      onChange={(e) => setSelectedPlan({ ...selectedPlan, engDescripsion: e.target.value })}
                      rows={3}
                      bg={colors.inputBg}
                      color={colors.textColor}
                      borderColor={colors.inputBorder}
                      _focus={{ borderColor: colors.inputFocus, boxShadow: `0 0 0 1px ${colors.inputFocus}` }}
                      _hover={{ borderColor: colors.inputFocus }}
                      sx={{
                        "&": {
                          bg: `${colors.inputBg} !important`,
                          color: `${colors.textColor} !important`,
                        },
                      }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>{isArabic ? "السعر (IQD)" : "Price (IQD)"}</FormLabel>
                    <NumberInput
                      value={selectedPlan.price}
                      min={0}
                      step={1}
                      precision={0}
                    >
                      <NumberInputField
                        onChange={(e) => setSelectedPlan({
                          ...selectedPlan, price: parseInt(e.target.value, 10) || 0
                        })}
                        bg={colors.inputBg}
                        color={colors.textColor}
                        borderColor={colors.inputBorder}
                        _focus={{ borderColor: colors.inputFocus, boxShadow: `0 0 0 1px ${colors.inputFocus}` }}
                        _hover={{ borderColor: colors.inputFocus }}
                        sx={{
                          "&": {
                            bg: `${colors.inputBg} !important`,
                            color: `${colors.textColor} !important`,
                          },
                        }}
                      />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                  <FormControl>
                    <FormLabel>{isArabic ? "الحالة" : "Status"}</FormLabel>
                    <Switch
                      isChecked={selectedPlan.isActive === 1}
                      onChange={(e) => setSelectedPlan({ ...selectedPlan, isActive: e.target.checked ? 1 : 0 })}
                    >
                      {selectedPlan.isActive === 1 ? (isArabic ? "نشطة" : "Active") : (isArabic ? "موقوفة" : "Inactive")}
                    </Switch>
                  </FormControl>
                </SimpleGrid>

                <Divider />

                <Text fontWeight="bold" fontSize="lg">{isArabic ? "المميزات" : "Features"}</Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                    <FormControl key={`note${n}`}>
                      <FormLabel fontSize="sm">{isArabic ? `ميزة ${n} (عربي)` : `Feature ${n} (Arabic)`}</FormLabel>
                      <Input
                        value={selectedPlan[`note${n}`] || ""}
                        onChange={(e) => setSelectedPlan({ ...selectedPlan, [`note${n}`]: e.target.value })}
                        bg={colors.inputBg}
                        color={colors.textColor}
                        borderColor={colors.inputBorder}
                        _focus={{ borderColor: colors.inputFocus, boxShadow: `0 0 0 1px ${colors.inputFocus}` }}
                        _hover={{ borderColor: colors.inputFocus }}
                        sx={{
                          "&": {
                            bg: `${colors.inputBg} !important`,
                            color: `${colors.textColor} !important`,
                          },
                        }}
                      />
                    </FormControl>
                  ))}
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                    <FormControl key={`engNote${n}`}>
                      <FormLabel fontSize="sm">{isArabic ? `ميزة ${n} (إنجليزي)` : `Feature ${n} (English)`}</FormLabel>
                      <Input
                        value={selectedPlan[`engNote${n}`] || ""}
                        onChange={(e) => setSelectedPlan({ ...selectedPlan, [`engNote${n}`]: e.target.value })}
                        bg={colors.inputBg}
                        color={colors.textColor}
                        borderColor={colors.inputBorder}
                        _focus={{ borderColor: colors.inputFocus, boxShadow: `0 0 0 1px ${colors.inputFocus}` }}
                        _hover={{ borderColor: colors.inputFocus }}
                        sx={{
                          "&": {
                            bg: `${colors.inputBg} !important`,
                            color: `${colors.textColor} !important`,
                          },
                        }}
                      />
                    </FormControl>
                  ))}
                </SimpleGrid>
              </VStack>
            </ModalBody>
            <ModalFooter gap={3}>
              <Button
                colorScheme="blue"
                onClick={handleSave}
                isLoading={saving}
                bg={colors.buttonBg}
                color={colors.buttonText}
                _hover={{ bg: colors.buttonHover }}
              >
                {isArabic ? "حفظ" : "Save"}
              </Button>
              <Button
                variant="ghost"
                onClick={() => setIsModalOpen(false)}
                color={colors.textColor}
                _hover={{ bg: colors.hoverBg }}
              >
                {isArabic ? "إلغاء" : "Cancel"}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </PageContainer>
  );
}